/*
 * Copyright (c) 2026 Joseph Duggan. All rights reserved.
 */

import { t } from '@sidekick/copy';
import { appPalette } from '@sidekick/design-tokens';
import { useEffect, useState } from 'react';
import { PageHeader } from '../components/PageHeader.js';
import { Goals } from '../components/regions/Goals.js';
import { Projects } from '../components/regions/Projects.js';
import { Stakeholders } from '../components/regions/Stakeholders.js';
import { inputStyle, monoLabel } from '../lib/ui.js';
import { trpc } from '../lib/trpc.js';

const p = appPalette;

/**
 * The Constitution — the durable context your Second holds.
 *
 * Goals and active projects sit above the day, alongside the identity
 * form: who you are, what you value, what you're building toward. Fields
 * here feed the morning prompt's user-context block, so even a few
 * sentences meaningfully sharpens The Letter.
 */
export function ConstitutionPage() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
      <PageHeader label={t.nav.constitution} />

      <div style={{ flex: 1, display: 'flex', justifyContent: 'center', padding: '40px 22px' }}>
        <div
          style={{
            width: '100%',
            maxWidth: 620,
            display: 'flex',
            flexDirection: 'column',
            gap: 40,
          }}
        >
          <Goals />
          <Projects />
          <Stakeholders />
          <Identity />
        </div>
      </div>
    </div>
  );
}

function Identity() {
  const utils = trpc.useUtils();
  const profileQuery = trpc.profile.get.useQuery();
  const update = trpc.profile.update.useMutation({
    onSuccess: async () => {
      await utils.profile.get.invalidate();
    },
  });

  const [identityName, setIdentityName] = useState('');
  const [roleTitle, setRoleTitle] = useState('');
  const [employer, setEmployer] = useState('');
  const [valuesText, setValuesText] = useState('');
  const [aspirationsText, setAspirationsText] = useState('');
  const [freeTextContext, setFreeTextContext] = useState('');
  const [seeded, setSeeded] = useState(false);

  // Seed once when the query first resolves; subsequent updates don't
  // clobber in-progress edits.
  useEffect(() => {
    if (seeded || !profileQuery.data) return;
    const d = profileQuery.data;
    setIdentityName(d.identityName ?? '');
    setRoleTitle(d.roleTitle ?? '');
    setEmployer(d.employer ?? '');
    setValuesText(d.values.join('\n'));
    setAspirationsText(d.aspirations.join('\n'));
    setFreeTextContext(d.freeTextContext ?? '');
    setSeeded(true);
  }, [profileQuery.data, seeded]);

  const onSave = () => {
    update.mutate({
      identityName: identityName.trim() || null,
      roleTitle: roleTitle.trim() || null,
      employer: employer.trim() || null,
      values: linesToList(valuesText),
      aspirations: linesToList(aspirationsText),
      freeTextContext: freeTextContext.trim() || null,
    });
  };

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {profileQuery.isPending ? (
        <span style={monoLabel}>…</span>
      ) : (
        <>
          <Field label="Name">
            <input
              type="text"
              value={identityName}
              onChange={(e) => setIdentityName(e.target.value)}
              placeholder="What your Second calls you"
              style={inputStyle}
            />
          </Field>

          <Field label="Role">
            <input
              type="text"
              value={roleTitle}
              onChange={(e) => setRoleTitle(e.target.value)}
              placeholder="e.g. Product Manager"
              style={inputStyle}
            />
          </Field>

          <Field label="Employer">
            <input
              type="text"
              value={employer}
              onChange={(e) => setEmployer(e.target.value)}
              placeholder="e.g. Acme"
              style={inputStyle}
            />
          </Field>

          <Field
            label="Values"
            hint="One per line. Non-negotiables your Second should defend on your behalf."
          >
            <textarea
              value={valuesText}
              onChange={(e) => setValuesText(e.target.value)}
              rows={4}
              placeholder="deep work&#10;directness&#10;family before output"
              style={{ ...inputStyle, resize: 'vertical', minHeight: 90 }}
            />
          </Field>

          <Field label="Aspirations" hint="One per line. What you're building toward.">
            <textarea
              value={aspirationsText}
              onChange={(e) => setAspirationsText(e.target.value)}
              rows={4}
              placeholder="ship a product I'm proud of&#10;write more"
              style={{ ...inputStyle, resize: 'vertical', minHeight: 90 }}
            />
          </Field>

          <Field
            label="Additional context"
            hint="Anything else your Second should hold in mind — projects, season, constraints."
          >
            <textarea
              value={freeTextContext}
              onChange={(e) => setFreeTextContext(e.target.value)}
              rows={5}
              placeholder="Recovering from a sprint. Two kids. Caregiving load this month."
              style={{ ...inputStyle, resize: 'vertical', minHeight: 110 }}
            />
          </Field>

          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 8 }}>
            <button
              type="button"
              onClick={onSave}
              disabled={update.isPending}
              style={{
                padding: '10px 18px',
                background: p.fg,
                color: p.bg,
                border: 'none',
                fontSize: 13,
                cursor: update.isPending ? 'wait' : 'pointer',
                opacity: update.isPending ? 0.6 : 1,
              }}
            >
              {update.isPending ? 'Saving…' : 'Save →'}
            </button>
            {update.isSuccess && !update.isPending ? (
              <span style={monoLabel}>Saved</span>
            ) : null}
            {update.error ? (
              <span style={{ ...monoLabel, color: p.accent }}>{update.error.message}</span>
            ) : null}
          </div>
        </>
      )}
    </section>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={monoLabel}>{label}</span>
      {hint ? (
        <span
          style={{
            fontFamily: 'var(--font-serif)',
            fontFeatureSettings: 'var(--serif-feat)',
            fontSize: 14,
            color: p.muted,
            margin: 0,
          }}
        >
          {hint}
        </span>
      ) : null}
      {children}
    </label>
  );
}

function linesToList(text: string): string[] {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}
